/**
 * formatApprovalPolicySummary.js — Pure function for a one-line machine-generated
 * summary of an approval policy. No hooks, no DOM, no i18n.
 * Used by ApprovalPoliciesPage list table as subtitle under policy name.
 *
 * @param {object} policy - Policy object from list API (includes policy_json or definition)
 * @returns {string} Single-line summary: "{tierCount} tiers · {amountLabel} · {stepsLabel}"
 */

function fmtAmount(n, currency) {
  if (n == null) return null;
  const code = currency || "EUR";
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return String(n);
  }
}

function deriveAmountLabel(tiers, currency) {
  const hasAnyAmount = tiers.some(
    (t) =>
      t?.any_amount === true ||
      (t?.min_amount == null && t?.max_amount == null && t?.any_amount !== false)
  );
  if (hasAnyAmount) return "Any amount";

  const mins = tiers
    .map((t) => (t?.min_amount != null ? t.min_amount : null))
    .filter((n) => n != null);
  const maxs = tiers
    .map((t) => (t?.max_amount != null ? t.max_amount : null))
    .filter((n) => n != null);
  const minVal = mins.length > 0 ? Math.min(...mins) : null;
  const maxVal = maxs.length > 0 ? Math.max(...maxs) : null;

  if (minVal != null && maxVal != null) {
    const smin = fmtAmount(minVal, currency);
    const smax = fmtAmount(maxVal, currency);
    return smin && smax ? `${smin} – ${smax}` : "Amount not set";
  }
  if (minVal != null) {
    const s = fmtAmount(minVal, currency);
    return s ? `≥ ${s}` : "Amount not set";
  }
  if (maxVal != null) {
    const s = fmtAmount(maxVal, currency);
    return s ? `≤ ${s}` : "Amount not set";
  }
  return "Amount not set";
}

function getMaxSteps(tiers) {
  const stepCounts = tiers.map((t) => {
    const steps = t?.chain ?? t?.steps ?? [];
    return Array.isArray(steps) ? steps.length : 0;
  });
  return stepCounts.length > 0 ? Math.max(...stepCounts) : 0;
}

function deriveStepsLabel(tiers) {
  const maxSteps = getMaxSteps(tiers);
  return maxSteps === 0 ? "no steps" : `up to ${maxSteps} steps`;
}

/**
 * Returns structured summary parts for chip/metadata rendering.
 * Reuses the same derivation logic as formatApprovalPolicySummary.
 *
 * @param {object} policy - Policy object from list API
 * @returns {{ tierCount: number, amountLabel: string, stepsLabel: string }}
 */
export function getApprovalPolicySummaryParts(policy) {
  if (!policy || typeof policy !== "object") {
    return { tierCount: 0, amountLabel: "Amount not set", stepsLabel: "No steps" };
  }

  const raw = policy.policy_json || policy.definition || policy;
  const tiers = Array.isArray(raw?.tiers) ? raw.tiers : [];
  const currency = raw?.currency ?? "EUR";

  const tierCount = tiers.length;
  const amountLabel = deriveAmountLabel(tiers, currency);
  const maxSteps = getMaxSteps(tiers);
  const stepsLabel = maxSteps === 0 ? "No steps" : `≤${maxSteps} steps`;

  return { tierCount, amountLabel, stepsLabel };
}

export function formatApprovalPolicySummary(policy) {
  if (!policy || typeof policy !== "object") {
    return "0 tiers · Amount not set · no steps";
  }

  const raw = policy.policy_json || policy.definition || policy;
  const tiers = Array.isArray(raw?.tiers) ? raw.tiers : [];
  const currency = raw?.currency ?? "EUR";

  const tierCount = tiers.length;
  const amountLabel = deriveAmountLabel(tiers, currency);
  const stepsLabel = deriveStepsLabel(tiers);

  return `${tierCount} tiers · ${amountLabel} · ${stepsLabel}`;
}
